import {Image} from '../images/Image';
import ViewportMgr from '../view/ViewportMgr';
import {getAppOrigin} from '../../utils/url';

const CF_THUMBNAIL_MAX_SIZE = 2000;
const CF_PLACEHOLDER_SIZE = 64;
const CF_DEFAULT_WIDTH = 1920;

/**
 * Represents a Cloudflare Stream video thumbnail implementing the Image interface
 */
class CloudflareStreamThumbnail implements Image {
	private readonly videoId: string;
	private readonly customThumbnail: string | null;

	/**
	 * @constructor
	 * @param {string} videoId - Cloudflare Stream video identifier
	 * @param {string} customThumbnail - Optional custom thumbnail URL
	 */
	constructor(videoId: string, customThumbnail?: string) {
		this.videoId = videoId;
		this.customThumbnail = customThumbnail || null;
	}

	/**
	 * Returns the pixel size for a given breakpoint and dpi, capped at CF_THUMBNAIL_MAX_SIZE
	 * @param {string} breakPoint - the breakpoint key
	 * @param {number} dpi - the desired dpi multiplier
	 * @return {number} the calculated pixel size
	 */
	private getBreakpointSize(breakPoint: string, dpi: number = 1): number {
		const systemBreakPoints = ViewportMgr.getBreakpointsMap(true);
		const breakPointsKeys = Object.keys(systemBreakPoints);
		const breakPointIndex = breakPointsKeys.indexOf(breakPoint);
		const nextBreakPointIndex =
			breakPointIndex < breakPointsKeys.length - 1 ? breakPointIndex + 1 : breakPointIndex;

		return Math.min(
			Math.round(systemBreakPoints[breakPointsKeys[nextBreakPointIndex]] * dpi),
			CF_THUMBNAIL_MAX_SIZE,
		);
	}

	/**
	 * Builds the proxied thumbnail URL with the given width
	 * @param {number} width - the desired thumbnail width
	 * @return {string} the thumbnail URL
	 */
	private buildThumbnailUrl(width: number): string {
		return `${getAppOrigin()}/mobify/cloudflare-video/thumbnail?videoId=${this.videoId}&width=${width}`;
	}

	/**
	 * Returns the thumbnail URL for the given breakpoint and dpi
	 * @param {string} breakPoint - the breakpoint key
	 * @param {number} dpi - the desired dpi multiplier
	 * @return {string} the thumbnail URL
	 */
	getUrl(breakPoint?: string, dpi: number = 1): string {
		if (this.customThumbnail) {
			return this.customThumbnail;
		}

		if (!breakPoint) {
			return this.buildThumbnailUrl(CF_DEFAULT_WIDTH);
		}

		const size = this.getBreakpointSize(breakPoint, dpi);

		return this.buildThumbnailUrl(size);
	}

	/**
	 * Returns the low-resolution placeholder thumbnail URL
	 * @return {string} the placeholder URL
	 */
	getPlaceholderUrl(): string {
		if (this.customThumbnail) {
			return this.customThumbnail;
		}

		return this.buildThumbnailUrl(CF_PLACEHOLDER_SIZE);
	}

	/**
	 * Returns the image width
	 * @return {string} undefined as width is determined by Cloudflare
	 */
	getWidth(): string {
		return undefined;
	}

	/**
	 * Returns the image height
	 * @return {string} undefined as height is determined by Cloudflare
	 */
	getHeight(): string {
		return undefined;
	}

	/**
	 * Returns the image title
	 * @return {string} empty string
	 */
	getTitle(): string {
		return '';
	}

	/**
	 * Returns the image alt text
	 * @return {string} empty string
	 */
	getAlt(): string {
		return '';
	}
}

export default CloudflareStreamThumbnail;
